import {
    Badge,
    Box,
    Flex,
    HStack,
    Icon,
    SimpleGrid,
    Stack,
    Text,
} from '@chakra-ui/react';
import { LuArrowDown, LuArrowRight, LuCircleCheck, LuFileCheck2, LuLandmark, LuWalletCards } from 'react-icons/lu';
import { useI18n } from 'src/i18n/I18nContext';

const demoAmounts = {
    budget: 480000,
    commitments: 285000,
    allocated: 118000,
    gap: 77000,
    transfers: 184000,
    expenses: 96000,
};

const ProjectFundingVisual = () => {
    const { language, t } = useI18n();
    const formatCurrency = (amount: number) => new Intl.NumberFormat(
        language === 'de' ? 'de-CH' : 'en-CH',
        { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 },
    ).format(amount);

    const metrics = [
        {
            key: 'budget',
            label: t('hero.visual.budget'),
            value: demoAmounts.budget,
            color: '#2563eb',
            background: '#eff6ff',
        },
        {
            key: 'commitments',
            label: t('hero.visual.commitments'),
            value: demoAmounts.commitments,
            color: '#0f766e',
            background: '#f0fdfa',
        },
        {
            key: 'allocated',
            label: t('hero.visual.allocated'),
            value: demoAmounts.allocated,
            color: '#047857',
            background: '#ecfdf5',
        },
        {
            key: 'gap',
            label: t('hero.visual.gap'),
            value: demoAmounts.gap,
            color: '#b45309',
            background: '#fffbeb',
        },
    ];

    const flow = [
        {
            key: 'secured',
            label: t('hero.visual.secured'),
            value: demoAmounts.commitments + demoAmounts.allocated,
            icon: LuCircleCheck,
        },
        {
            key: 'transfers',
            label: t('hero.visual.transfers'),
            value: demoAmounts.transfers,
            icon: LuLandmark,
        },
        {
            key: 'expenses',
            label: t('hero.visual.expenses'),
            value: demoAmounts.expenses,
            icon: LuFileCheck2,
        },
    ];

    return (
        <Box
            role='img'
            aria-label={t('hero.visual.alt')}
            w='full'
            overflow='hidden'
            rounded={{ base: 'xl', md: '2xl' }}
            borderWidth='1px'
            borderColor='#cbd5e1'
            bg='#f8fafc'
            color='#0f172a'
            boxShadow='0 24px 70px -34px rgba(15, 23, 42, 0.55)'
        >
            <Flex
                minH='3rem'
                align='center'
                justify='space-between'
                gap='3'
                px={{ base: 4, md: 5 }}
                borderBottomWidth='1px'
                borderColor='#e2e8f0'
                bg='white'
            >
                <HStack gap='3'>
                    <HStack gap='1.5' aria-hidden='true'>
                        <Box boxSize='2' rounded='full' bg='#f87171' />
                        <Box boxSize='2' rounded='full' bg='#fbbf24' />
                        <Box boxSize='2' rounded='full' bg='#34d399' />
                    </HStack>
                    <Text fontWeight='bold' fontSize='sm' letterSpacing='tight'>Peregin</Text>
                </HStack>
                <Badge colorPalette='blue' variant='subtle' rounded='full' px='2.5'>
                    {t('hero.visual.demoData')}
                </Badge>
            </Flex>

            <Stack gap={{ base: 4, md: 5 }} p={{ base: 4, sm: 5, md: 7 }}>
                <Flex align={{ base: 'flex-start', md: 'center' }} justify='space-between' gap='3' direction={{ base: 'column', md: 'row' }}>
                    <Stack gap='0.5' textAlign='left'>
                        <Text color='#64748b' fontSize='xs' fontWeight='bold' textTransform='uppercase' letterSpacing='wide'>
                            {t('hero.visual.title')}
                        </Text>
                        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight='bold'>
                            {t('hero.visual.projectName')}
                        </Text>
                    </Stack>
                    <HStack color='#475569' fontSize='sm' gap='2'>
                        <Text>{t('hero.visual.period')}</Text>
                        <Text aria-hidden='true'>•</Text>
                        <Text>CHF</Text>
                    </HStack>
                </Flex>

                <Box bg='white' borderWidth='1px' borderColor='#e2e8f0' rounded='xl' p={{ base: 4, md: 5 }}>
                    <Flex align='baseline' justify='space-between' gap='3' mb='3'>
                        <Text fontSize='sm' fontWeight='semibold'>{t('hero.visual.coverage')}</Text>
                        <Text color='#0f766e' fontSize={{ base: 'xl', md: '2xl' }} fontWeight='bold'>84%</Text>
                    </Flex>
                    <HStack h='0.75rem' gap='0' overflow='hidden' rounded='full' bg='#e2e8f0' aria-hidden='true'>
                        <Box w='59.4%' h='full' bg='#0f766e' />
                        <Box w='24.6%' h='full' bg='#34d399' />
                        <Box w='16%' h='full' bg='#f59e0b' />
                    </HStack>
                    <SimpleGrid columns={{ base: 1, sm: 3 }} gap={{ base: 2, sm: 4 }} mt='3'>
                        {[
                            { label: t('hero.visual.commitments'), color: '#0f766e' },
                            { label: t('hero.visual.allocated'), color: '#34d399' },
                            { label: t('hero.visual.gap'), color: '#f59e0b' },
                        ].map((item) => (
                            <HStack key={item.label} gap='2' color='#475569' fontSize='xs'>
                                <Box boxSize='2' flexShrink={0} rounded='full' bg={item.color} />
                                <Text>{item.label}</Text>
                            </HStack>
                        ))}
                    </SimpleGrid>
                </Box>

                <SimpleGrid columns={{ base: 2, md: 4 }} gap={{ base: 2.5, md: 3 }}>
                    {metrics.map((metric) => (
                        <Stack
                            key={metric.key}
                            minH={{ base: '5.5rem', md: '6.25rem' }}
                            justify='space-between'
                            gap='2'
                            p={{ base: 3, md: 4 }}
                            rounded='xl'
                            borderWidth='1px'
                            borderColor='#e2e8f0'
                            borderTopWidth='3px'
                            borderTopColor={metric.color}
                            bg={metric.background}
                            textAlign='left'
                        >
                            <Text color='#475569' fontSize={{ base: 'xs', md: 'sm' }} fontWeight='semibold' lineHeight='1.25'>
                                {metric.label}
                            </Text>
                            <Text color={metric.color} fontSize={{ base: 'sm', sm: 'md', lg: 'lg' }} fontWeight='bold' whiteSpace='nowrap'>
                                {formatCurrency(metric.value)}
                            </Text>
                        </Stack>
                    ))}
                </SimpleGrid>

                <Box bg='white' borderWidth='1px' borderColor='#e2e8f0' rounded='xl' p={{ base: 4, md: 5 }}>
                    <HStack mb='3' gap='2' color='#334155'>
                        <Icon as={LuWalletCards} />
                        <Text fontSize='sm' fontWeight='semibold'>{t('hero.visual.traceability')}</Text>
                    </HStack>
                    <Flex align='stretch' direction={{ base: 'column', sm: 'row' }} gap={{ base: 2, sm: 3 }}>
                        {flow.map((step, index) => (
                            <Flex key={step.key} flex='1' align='center' gap={{ base: 2, sm: 3 }}>
                                <HStack flex='1' minW='0' p='3' rounded='lg' bg='#f8fafc' borderWidth='1px' borderColor='#e2e8f0' gap='3'>
                                    <Flex boxSize='8' flexShrink={0} align='center' justify='center' rounded='full' bg='#e0f2fe' color='#0369a1'>
                                        <Icon as={step.icon} />
                                    </Flex>
                                    <Stack gap='0' minW='0' textAlign='left'>
                                        <Text color='#64748b' fontSize='xs' lineClamp={1}>{step.label}</Text>
                                        <Text fontSize='sm' fontWeight='bold' whiteSpace='nowrap'>{formatCurrency(step.value)}</Text>
                                    </Stack>
                                </HStack>
                                {index < flow.length - 1 && (
                                    <Icon
                                        as={LuArrowRight}
                                        display={{ base: 'none', sm: 'block' }}
                                        flexShrink={0}
                                        color='#94a3b8'
                                        aria-hidden='true'
                                    />
                                )}
                                {index < flow.length - 1 && (
                                    <Icon
                                        as={LuArrowDown}
                                        display={{ base: 'block', sm: 'none' }}
                                        flexShrink={0}
                                        color='#94a3b8'
                                        aria-hidden='true'
                                    />
                                )}
                            </Flex>
                        ))}
                    </Flex>
                </Box>
            </Stack>
        </Box>
    );
};

export default ProjectFundingVisual;
